import { useCallback, useMemo, useRef } from 'react';

const INTENT_DELAY_MS = 120;

type IntentHandlers = {
  onMouseEnter: () => void;
  onFocus: () => void;
  onTouchStart: () => void;
  onMouseLeave: () => void;
  onBlur: () => void;
  onTouchEnd: () => void;
};

const supportsFastNetwork = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const connection = (
    navigator as Navigator & {
      connection?: {
        saveData?: boolean;
        effectiveType?: string;
      };
    }
  ).connection;

  if (!connection) return true;
  if (connection.saveData) return false;
  if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') return false;
  return true;
};

export const useIntentPrefetch = () => {
  const timerMapRef = useRef(new Map<string, number>());
  const canPrefetch = useMemo(() => supportsFastNetwork(), []);

  const cancelPrefetch = useCallback((key: string) => {
    const timerId = timerMapRef.current.get(key);
    if (timerId) {
      window.clearTimeout(timerId);
      timerMapRef.current.delete(key);
    }
  }, []);

  const schedulePrefetch = useCallback(
    (key: string, action: () => void) => {
      if (!canPrefetch || typeof window === 'undefined') return;
      cancelPrefetch(key);

      const timerId = window.setTimeout(() => {
        timerMapRef.current.delete(key);
        action();
      }, INTENT_DELAY_MS);

      timerMapRef.current.set(key, timerId);
    },
    [canPrefetch, cancelPrefetch]
  );

  const getIntentHandlers = useCallback(
    (key: string, action: () => void): IntentHandlers => ({
      onMouseEnter: () => schedulePrefetch(key, action),
      onFocus: () => schedulePrefetch(key, action),
      onTouchStart: () => schedulePrefetch(key, action),
      onMouseLeave: () => cancelPrefetch(key),
      onBlur: () => cancelPrefetch(key),
      onTouchEnd: () => cancelPrefetch(key),
    }),
    [cancelPrefetch, schedulePrefetch]
  );

  return {
    getIntentHandlers,
    prefetchNow: schedulePrefetch,
    canPrefetch,
  };
};
